import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { useAppConfig } from '../hooks/config/useAppConfig'
import { useAuth } from './AuthContext'
import { getFromToken, JwtKeys } from '../utils/jwtUtils'

import { AppConfig, ConfigContextType } from '../types/contexts/ConfigContext'

const ConfigCtx = createContext<ConfigContextType | null>(null)

export const useConfig = (): ConfigContextType => {
  const ctx = useContext(ConfigCtx)

  if (!ctx) {
    throw new Error('useConfig must be used inside ConfigProvider')
  }

  return ctx
}

interface ConfigProviderProps {
  children: ReactNode
}

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const { appConfig } = useAppConfig()
  // Always call with the current access token (it is refreshed while the user stays signed in).
  const appConfigRef = useRef(appConfig)
  appConfigRef.current = appConfig
  const { authInfo } = useAuth()
  // The booking window is the signed-in user's own: load the config again when the user changes (login, logout).
  const userId = authInfo?.accessToken ? getFromToken(authInfo.accessToken, JwtKeys.SUB) : undefined

  const refreshConfig = useCallback(async () => {
    try {
      const data = await appConfigRef.current()
      if (data) {
        setConfig(data)
      }
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloads when the signed-in user changes
  }, [userId])

  useEffect(() => {
    void refreshConfig()
  }, [refreshConfig])

  return (
    <ConfigCtx.Provider value={{ config, loading, refreshConfig }}>
      {children}
    </ConfigCtx.Provider>
  )
}
